import React, { useEffect, useRef, useState } from 'react';

import { v4 as uuid } from 'uuid';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Button, IconButton, ImageList, ImageListItem, Stack, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { Reservation } from '@luxuryescapes/contract-svc-reservation';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import RoomTypeImagesForm, { RoomTypeImage } from '~/components/Common/Forms/RoomTypeImagesForm';
import { buttonMessages as baseButtonMessages, buttonStates } from '~/components/Common/Forms/states/submitButton';
import Image from '~/components/Common/Image';
import { FlatImageEditorHandle } from '~/components/Common/Images/types';

const buttonMessages = { ...baseButtonMessages };
buttonMessages[buttonStates.default] = 'Save changes';

type ApiListProps = {
  images: Array<ApiImageData>;
  toggleImageSelection: (id: string, oldValue: boolean) => void;
};

function ApiList({ images, toggleImageSelection }: ApiListProps) {
  return (
    <ImageList cols={4} gap={10} sx={{ mt: 2 }}>
      {images.map((item) => (
        <ImageListItem
          key={item.id_cloudinary_external}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            toggleImageSelection(item.id, item.selected);
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              position: 'absolute',
              right: 10,
              zIndex: 1,
            }}
          >
            <IconButton
              color="info"
              title={item.selected ? 'Select Image' : 'Unselect Image'}
              sx={{
                zIndex: 2,
              }}
            >
              {item.selected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            </IconButton>
          </Box>
          <Image
            className="img-responsive mx-auto"
            publicId={item.id_cloudinary_external}
            options={{
              width: 266,
              height: 200,
            }}
          />
          <Box m={2} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography>{item.title}</Typography>
          </Box>
        </ImageListItem>
      ))}
    </ImageList>
  );
}

interface ApiImageData extends Reservation.Image {
  selected: boolean;
}

interface Props {
  images: Array<Reservation.Image>;
  apiImages: Array<Reservation.Image>;
  onImagesChange: (images: Array<Reservation.Image>) => void;
  saveImages: (images: Array<RoomTypeImage>) => Promise<void>;
}

function RentalImagesForm(props: Props) {
  const [apiImages, setApiImages] = useState<Array<ApiImageData>>([]);
  const [heroImages, setHeroImages] = useState<Array<Reservation.Image>>([]);
  const heroImagesRef = useRef<Array<Reservation.Image>>([]);
  const imagesFormRef = useRef<FlatImageEditorHandle | null>(null);
  const [saveButtonState, setSaveButtonState] = useState(buttonStates.saved);
  const isButtonStateSaving = saveButtonState === buttonStates.saving;

  useEffect(() => {
    const nextHeroImages = [...props.images];
    heroImagesRef.current = nextHeroImages;
    setHeroImages(nextHeroImages);
    setApiImages(
      props.apiImages.map<ApiImageData>((image) => ({
        ...image,
        selected: false,
      })),
    );
  }, [props.apiImages, props.images]);

  const sortImages = (images) => {
    return [...images]
      .map((i) => ({
        ...i,
        publicImageId: i.id_cloudinary_external,
      }))
      .sort((a, b) => {
        if (a.order > b.order) {
          return 1;
        }
        if (a.order < b.order) {
          return -1;
        }
        return 0;
      });
  };

  const onCreateImage = async (imageServiceId: string) => {
    const imageData = {
      id: uuid(), // All array manipulation is done by id, so we need to generate a unique id for unsaved images
      id_cloudinary_external: imageServiceId,
      title: 'New image',
      order: heroImages.length + 1,
    };
    return imageData;
  };

  const onUpdateImages = async (images: Array<any>) => {
    setSaveButtonState(buttonStates.default);
    heroImagesRef.current = images;
    setHeroImages(images);
  };

  const onSaveImages = async () => {
    setSaveButtonState(buttonStates.saving);

    const uploadResult = await imagesFormRef.current?.uploadAllPending?.();

    if (uploadResult?.failedCount) {
      setSaveButtonState(buttonStates.failed);
      return;
    }

    props
      .saveImages(heroImagesRef.current)
      .then(() => {
        setSaveButtonState(buttonStates.saved);
      })
      .catch(() => {
        setSaveButtonState(buttonStates.failed);
      });
  };

  const moveImagesToHero = () => {
    const newImages = [...apiImages]
      .filter((i) => i.selected)
      .map((p, index) => ({
        ...p,
        order: heroImages.length + index + 1,
      }));
    setApiImages([...apiImages].filter((i) => !i.selected));
    onUpdateImages([...heroImages, ...newImages]);
  };

  const toggleImageSelection = (id, oldValue) => {
    const newImages = [...apiImages].map((p) => ({
      ...p,
      selected: p.id === id ? !oldValue : p.selected,
    }));
    setApiImages(newImages);
  };

  const allSelected = apiImages.length > 0 && apiImages.every((i) => i.selected);

  const toggleSelectAll = () => {
    setApiImages(apiImages.map((img) => ({ ...img, selected: !allSelected })));
  };

  return (
    <>
      <PageSubheader title="Property Images" />

      <Box>
        <Typography variant="h4">Hero images</Typography>
        <div style={{ justifyContent: 'end' }}>
          <Button variant="contained" onClick={onSaveImages} disabled={isButtonStateSaving}>
            {buttonMessages[saveButtonState]}
          </Button>
        </div>
      </Box>

      <RoomTypeImagesForm
        ref={imagesFormRef}
        enablePendingCropUpload
        images={sortImages(heroImages)}
        hasHiddenToggle
        onAddImage={onCreateImage}
        onUpdateImages={onUpdateImages}
        hideDeleteButton
      />

      {apiImages.length > 0 && (
        <>
          <Box mt={7}>
            <Typography variant="h4">API images</Typography>
          </Box>
          <ApiList images={apiImages} toggleImageSelection={toggleImageSelection} />
          <Stack direction="row" alignItems="center" mt={2}>
            <FormControlLabel
              control={<Checkbox color="info" checked={allSelected} onChange={toggleSelectAll} />}
              label={allSelected ? 'Deselect all' : 'Select all'}
            />
            <Button variant="contained" color="primary" onClick={moveImagesToHero}>
              Move to Hero images
            </Button>
          </Stack>
        </>
      )}
    </>
  );
}

export default RentalImagesForm;
